import { useState, useMemo } from 'react'
import { colors, formatNumber } from '../styles/tokens'
import { Button, Input, Select } from './FormControls'
import { calculateConcreteVolume } from '../lib/quantityEngine'
import { useDraftPersistence } from '../lib/useDraftPersistence'

export default function ConcreteVolumeModulePanel({ scopeId, boqItemId, superseding, onClose, onError, saving, onSave }) {
  const draftKey = `draft:concrete_volume:${scopeId || boqItemId || 'unknown'}`
  const [draft, setDraft, clearDraft] = useDraftPersistence(draftKey, { volumes: [], wastePct: '' })
  const initial = superseding ? { volumes: superseding.engineering_inputs?.volumes || [], wastePct: superseding.engineering_inputs?.wastePct ?? '' } : draft

  const [volumes, setVolumesRaw] = useState(initial.volumes)
  const [wastePct, setWastePctRaw] = useState(initial.wastePct)
  const setVolumes = (u) => setVolumesRaw((p) => { const n = typeof u === 'function' ? u(p) : u; if (!superseding) setDraft((d) => ({ ...d, volumes: n })); return n })
  const setWastePct = (v) => { setWastePctRaw(v); if (!superseding) setDraft((d) => ({ ...d, wastePct: v })) }

  function addVolume() { setVolumes((p) => [...p, { id: `V${Date.now()}`, label: '', geometrySource: 'area', area: '', length: '', width: '', thickness: '', openings: [] }]) }
  function updateVolume(id, patch) { setVolumes((p) => p.map((v) => (v.id === id ? { ...v, ...patch } : v))) }
  function removeVolume(id) { setVolumes((p) => p.filter((v) => v.id !== id)) }
  function addOpening(vid) { setVolumes((p) => p.map((v) => (v.id === vid ? { ...v, openings: [...v.openings, { id: `O${Date.now()}`, label: '', length: '', width: '' }] } : v))) }
  function updateOpening(vid, oid, patch) { setVolumes((p) => p.map((v) => (v.id === vid ? { ...v, openings: v.openings.map((o) => (o.id === oid ? { ...o, ...patch } : o)) } : v))) }
  function removeOpening(vid, oid) { setVolumes((p) => p.map((v) => (v.id === vid ? { ...v, openings: v.openings.filter((o) => o.id !== oid) } : v))) }

  const volumesForCalc = useMemo(() => volumes.map((v) => ({
    label: v.label, geometrySource: v.geometrySource,
    area: v.geometrySource === 'area' ? (v.area === '' ? NaN : Number(v.area)) : undefined,
    length: v.geometrySource === 'length_width' ? (v.length === '' ? NaN : Number(v.length)) : undefined,
    width: v.geometrySource === 'length_width' ? (v.width === '' ? NaN : Number(v.width)) : undefined,
    thickness: v.thickness === '' ? NaN : Number(v.thickness),
    openings: v.openings.map((o) => ({ label: o.label, length: Number(o.length), width: Number(o.width) })),
  })), [volumes])

  const result = useMemo(() => {
    if (volumesForCalc.length === 0) return { value: null, error: null }
    try { return { value: calculateConcreteVolume(volumesForCalc, { wastePct: wastePct === '' ? NaN : Number(wastePct) }), error: null } }
    catch (e) { return { value: null, error: e.message } }
  }, [volumesForCalc, wastePct])

  async function handleSave() {
    if (!result.value) return
    const engineeringInputs = { volumes, wastePct: Number(wastePct) }
    const succeeded = await onSave({
      calculatedValueTon: result.value.finalQuantity, unit: 'm3',
      geometrySnapshot: { items: [] }, engineeringInputs,
      breakdownSnapshot: { steps: result.value.steps, volumeBreakdown: result.value.volumeBreakdown },
      takeoffItemIdForProvenance: null,
    })
    if (succeeded !== false) clearDraft()
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase' }}>Concrete Elements</label>
        <Button size="sm" variant="secondary" onClick={addVolume}>+ Add Element</Button>
      </div>
      {volumes.map((v) => (
        <div key={v.id} style={{ border: `1px solid ${colors.border}`, borderRadius: 6, padding: 8, marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <Input placeholder="Label" value={v.label} onChange={(e) => updateVolume(v.id, { label: e.target.value })} style={{ flex: 1, fontSize: 11 }} />
            <button onClick={() => removeVolume(v.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer' }}>✕</button>
          </div>
          <LabeledSelect label="Geometry Source" value={v.geometrySource} onChange={(e) => updateVolume(v.id, { geometrySource: e.target.value })}>
            <option value="area">Area</option><option value="length_width">Length × Width</option>
          </LabeledSelect>
          {v.geometrySource === 'area' ? (
            <LabeledInput label="Area (m²)" type="number" value={v.area} onChange={(e) => updateVolume(v.id, { area: e.target.value })} />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <LabeledInput label="Length (m)" type="number" value={v.length} onChange={(e) => updateVolume(v.id, { length: e.target.value })} />
              <LabeledInput label="Width (m)" type="number" value={v.width} onChange={(e) => updateVolume(v.id, { width: e.target.value })} />
            </div>
          )}
          <LabeledInput label="Thickness (m)" type="number" value={v.thickness} onChange={(e) => updateVolume(v.id, { thickness: e.target.value })} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 4px' }}>
            <span style={{ fontSize: 11, fontWeight: 700 }}>Openings</span>
            <Button size="sm" variant="secondary" onClick={() => addOpening(v.id)}>+ Add Opening</Button>
          </div>
          {v.openings.map((o) => (
            <div key={o.id} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
              <Input placeholder="Label" value={o.label} onChange={(e) => updateOpening(v.id, o.id, { label: e.target.value })} style={{ flex: 1, fontSize: 11 }} />
              <Input type="number" placeholder="Length (m)" value={o.length} onChange={(e) => updateOpening(v.id, o.id, { length: e.target.value })} style={{ width: 90, fontSize: 11 }} />
              <Input type="number" placeholder="Width (m)" value={o.width} onChange={(e) => updateOpening(v.id, o.id, { width: e.target.value })} style={{ width: 90, fontSize: 11 }} />
              <button onClick={() => removeOpening(v.id, o.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer' }}>✕</button>
            </div>
          ))}
        </div>
      ))}
      <LabeledInput label="Waste % — required" type="number" value={wastePct} onChange={(e) => setWastePct(e.target.value)} />
      {result.value && (
        <div style={{ margin: '12px 0' }}>
          {result.value.volumeBreakdown.map((vb, i) => (
            <div key={i} style={{ border: `1px solid ${colors.border}`, borderRadius: 8, padding: 10, marginBottom: 8, background: '#FAFBFC', fontSize: 11 }}>
              <strong>{vb.volume.label}</strong>: Net Area {formatNumber(vb.netArea)} m² × {vb.volume.thickness}m = <strong>{formatNumber(vb.netVolume)} m³</strong>
            </div>
          ))}
          <div style={{ padding: '10px 12px', background: '#F8FAFF', border: `1px solid ${colors.primary}`, borderRadius: 8, fontWeight: 700 }}>
            Total: {formatNumber(result.value.finalQuantity)} m³
          </div>
        </div>
      )}
      {result.error && <div style={{ fontSize: 12, color: colors.textMuted, fontStyle: 'italic', marginBottom: 14 }}>{result.error}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {!superseding && <button onClick={() => { if (window.confirm('Discard draft?')) { clearDraft(); onClose() } }} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 12 }}>Discard Draft</button>}
        <Button onClick={handleSave} disabled={!result.value || saving} style={{ marginLeft: 'auto' }}>{saving ? 'Saving…' : superseding ? 'Save New Version' : 'Save Output'}</Button>
      </div>
    </div>
  )
}
function LabeledInput({ label, ...p }) { return <div style={{marginBottom:6}}><label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>{label}</label><Input {...p} style={{ fontSize: 11, width: '100%' }} /></div> }
function LabeledSelect({ label, children, ...p }) { return <div style={{marginBottom:6}}><label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>{label}</label><Select {...p} style={{ fontSize: 11, width: '100%' }}>{children}</Select></div> }
